import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import pushNotificationService, { PUSH_EVENTS } from '../services/pushNotifications';
import { useBumpTapEpoch } from '../contexts/ResumeContext';

/**
 * PushNotificationHandler - Handles navigation when push notifications are tapped
 * 
 * This component subscribes to push notification events and handles navigation
 * using React Router, keeping navigation logic in the React layer.
 *
 * It also bumps the `tapEpoch` on every tap so pages already mounted at the
 * target route (where React Router would otherwise no-op) can refetch.
 * See ResumeContext architecture (2026-02-29).
 */
const PushNotificationHandler = () => {
  const navigate = useNavigate();
  const bumpTapEpoch = useBumpTapEpoch();

  useEffect(() => {
    // Subscribe to notification tap events
    const unsubscribe = pushNotificationService.subscribe(
      PUSH_EVENTS.NOTIFICATION_TAP,
      (eventData) => {
        console.log('PushNotificationHandler: Received tap event', eventData);

        if (eventData.targetRoute) {
          // Use React Router to navigate
          navigate(eventData.targetRoute);
        }

        // Always bump tapEpoch — even if React Router no-ops (same path),
        // subscribed pages should rehydrate their data.
        try { bumpTapEpoch(); } catch { /* noop */ }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [navigate, bumpTapEpoch]);

  // This component doesn't render anything
  return null;
};

export default PushNotificationHandler;
